// Ouvrages types : onglet du catalogue (liste, jeu initial CVC, création et réglage des temps) et dialogue d'insertion dans un devis.
// Aucun prix ici : les fournitures prennent le prix daté du catalogue (référence exacte) ou restent « À renseigner ».
import { useEffect, useMemo, useState, type FormEvent } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Boxes, Download, Pencil, Plus, Search, Trash2 } from 'lucide-react';
import { api, messageErreur } from '../../lib/api.ts';
import { date, pluriel } from '../../lib/format.ts';
import {
  Alerte, Badge, Bouton, Champ, ChampChoix, ChampTexte, Chargement, Dialogue, EtatErreur, EtatVide, Panneau, useNotifier,
} from '../../components/ui.tsx';
import { heures, LIBELLES_SECTIONS, OPTIONS_SECTIONS, quantite, saisieValide, versSaisie, type Section, type VueRevision } from './commun.tsx';

export interface Composant {
  id: string; role: 'base' | 'penser_a_tout'; section: Section; label: string; reference: string | null; unit: string; qty_per_unit: string;
  hours_per_unit: string | null; supply: boolean; per_unit: boolean; time_code: string | null; note: string | null;
  /** L'étape de chantier issue de ce composant demandera une photo. */
  photo_required?: boolean;
}
export interface Ouvrage {
  id: string; code: string; label: string; category: string; unit: string; description: string | null; reminders: string | null;
  time_source: string; estimate: boolean; labor_rate_code: string | null; seed_key: string | null; dated: string; active: boolean;
  composants: Composant[]; heures: { par_unite: string | null; fixes: string | null; a_renseigner: number };
  /** Personnes minimum (null = non précisé). */
  crew_min?: number | null;
  /** Métier déduit de la catégorie (null = « Autres »). */
  metier?: Metier | null;
}
type Metier = 'cvc' | 'gtb' | 'elec';
interface ListeOuvrages { ouvrages: Ouvrage[]; categories: Record<string, string>; jeu_initial: { version: string; manquants: number }; droits: { ecrire: boolean } }
interface EtatJeu { metier: Metier; libelle: string; version: string; total: number; installes: number; manquants: number; a_completer: number; estimations: number }
interface JeuxInitiaux { jeux: EtatJeu[]; metiers: Record<Metier, string>; metier_par_categorie: Record<string, Metier | null>; droits: { ecrire: boolean } }

const cleListe = ['ouvrages'];
const cleJeux = ['ouvrages', 'jeux-initiaux'];
const chargerListe = () => api.get<ListeOuvrages>('/api/ouvrages');
const COURT: Record<Metier, string> = { cvc: 'CVC', gtb: 'GTB', elec: 'électricité' };

/** Badge du métier d'un ouvrage (CVC, GTB et domotique, Électricité). */
function BadgeMetier({ m }: { m?: Metier | null }) {
  if (!m) return null;
  return <Badge ton={m === 'cvc' ? 'info' : m === 'gtb' ? 'accent' : 'neutre'} icone={false}>{m === 'cvc' ? 'CVC' : m === 'gtb' ? 'GTB' : 'Électricité'}</Badge>;
}

/** Badges d'origine des temps : estimation à valider, temps à renseigner. */
function BadgesTemps({ o }: { o: Ouvrage }) {
  return (
    <>
      {o.estimate && <Badge ton="ambre">Estimation — à valider</Badge>}
      {o.heures.a_renseigner > 0 && <Badge ton="ambre">{o.heures.a_renseigner === o.composants.filter((c) => c.role === 'base').length ? 'Temps à renseigner' : 'Temps partiellement à renseigner'}</Badge>}
      {!o.active && <Badge>Désactivé</Badge>}
    </>
  );
}
const heuresOuvrage = (o: Ouvrage) => {
  if (o.heures.par_unite === null) return 'Temps à renseigner';
  const fixes = o.heures.fixes && Number(o.heures.fixes) > 0 ? ` + ${quantite(o.heures.fixes)} h fixes` : '';
  return `${quantite(o.heures.par_unite)} h / ${o.unit}${fixes}`;
};

// ═══ Onglet du catalogue ═══════════════════════════════════════════════════════════════════════
export function OngletOuvrages() {
  const qc = useQueryClient();
  const notifier = useNotifier();
  const r = useQuery({ queryKey: cleListe, queryFn: chargerListe });
  const jeux = useQuery({ queryKey: cleJeux, queryFn: () => api.get<JeuxInitiaux>('/api/ouvrages/jeux-initiaux') });
  const [filtre, setFiltre] = useState('');
  const [metier, setMetier] = useState<Metier | ''>('');
  const [edite, setEdite] = useState<Ouvrage | null | 'nouveau'>(null);
  const installer = useMutation({
    mutationFn: (m: Metier) => api.post<{ metier: Metier; crees: number; deja: number; completes: number }>(`/api/ouvrages/jeux-initiaux/${m}`),
    onSuccess: (x) => {
      const quoi = [x.crees ? pluriel(x.crees, 'ouvrage installé', 'ouvrages installés') : '', x.completes ? pluriel(x.completes, 'ouvrage complété', 'ouvrages complétés') : '']
        .filter(Boolean).join(', ') || 'rien à installer';
      notifier(`Jeu initial ${COURT[x.metier]} : ${quoi}. Aucun temps réglé par l’entreprise n’a été modifié.`);
      void qc.invalidateQueries({ queryKey: cleListe });
    },
    onError: (e) => notifier(messageErreur(e), true),
  });
  const ecrire = !!r.data?.droits.ecrire;
  const groupes = useMemo(() => {
    if (!r.data) return [];
    const f = filtre.trim().toLowerCase();
    const liste = r.data.ouvrages.filter((o) => (!metier || o.metier === metier) && (!f || `${o.code} ${o.label}`.toLowerCase().includes(f)));
    return Object.entries(r.data.categories).map(([cat, libelle]) => ({ cat, libelle, ouvrages: liste.filter((o) => o.category === cat) })).filter((g) => g.ouvrages.length);
  }, [r.data, filtre, metier]);
  const aInstaller = (jeux.data?.jeux ?? []).filter((j) => j.manquants > 0 || j.a_completer > 0);
  const compte = (m: Metier) => r.data?.ouvrages.filter((o) => o.metier === m).length ?? 0;
  const estimations = r.data?.ouvrages.filter((o) => o.estimate).length ?? 0;
  // Catalogue vide : l'installation des jeux est l'action principale, en tête (visible sans défiler sur téléphone).
  const vide = !!r.data && !r.data.ouvrages.length;
  const boutonsInstaller = ecrire && aInstaller.map((j) => (
    <Bouton key={j.metier} variante={vide && j.metier === aInstaller[0].metier ? 'principal' : 'secondaire'} icone={<Download aria-hidden />}
      chargement={installer.isPending && installer.variables === j.metier} disabled={installer.isPending} onClick={() => installer.mutate(j.metier)}>
      {j.manquants ? `Installer le jeu initial ${COURT[j.metier]} (${j.manquants})` : `Compléter les temps ${COURT[j.metier]} (${j.a_completer})`}
    </Bouton>
  ));
  return (
    <div className="pile-l">
      <Panneau serre titre={r.data ? pluriel(r.data.ouvrages.length, 'ouvrage type', 'ouvrages types') : 'Ouvrages types'} actions={
        <div className="groupe-btn">
          {vide && boutonsInstaller}
          {!vide && <>
            <label className="rangee" style={{ gap: 6 }}>
              <Search size={18} aria-hidden className="discret" />
              <span className="visuellement-cache">Filtrer les ouvrages</span>
              <input className="saisie devis-filtre" placeholder="Code ou libellé" value={filtre} onChange={(e) => setFiltre(e.target.value)} />
            </label>
            <label className="rangee" style={{ gap: 6 }}>
              <span className="visuellement-cache">Métier</span>
              <select className="saisie ouvrages-metier" value={metier} onChange={(e) => setMetier(e.target.value as Metier | '')}>
                <option value="">Tous les métiers</option>
                {(['cvc', 'gtb', 'elec'] as const).map((m) => <option key={m} value={m}>{`${jeux.data?.metiers[m] ?? COURT[m]} (${compte(m)})`}</option>)}
              </select>
            </label>
          </>}
          {ecrire && <Bouton variante={vide ? 'secondaire' : 'principal'} icone={<Plus aria-hidden />} onClick={() => setEdite('nouveau')}>Nouvel ouvrage</Bouton>}
          {!vide && boutonsInstaller}
        </div>}>
        {r.isLoading && <Chargement />}
        {r.isError && <EtatErreur erreur={r.error} reessayer={() => r.refetch()} />}
        {jeux.data && r.data && !!r.data.ouvrages.length && (
          <p className="tres-petit discret ouvrages-etat-jeux">
            Jeux initiaux : {jeux.data.jeux.map((j) => `${COURT[j.metier]} ${j.installes}/${j.total}`).join(' · ')}
            {estimations ? ` — ${pluriel(estimations, 'ouvrage aux temps estimés', 'ouvrages aux temps estimés')}, à valider par votre référent` : ''}.
          </p>
        )}
        {r.data && !r.data.ouvrages.length && (
          <EtatVide icone={<Boxes />} titre="Aucun ouvrage type"
            texte={ecrire ? 'Installez un jeu initial par métier — CVC (splits, DRV, aéraulique, PAC…), GTB et domotique (points, automates, passerelles, comptages, supervision), électricité (tableaux, circuits, terre, essais) — ou créez vos propres ouvrages.'
              : 'Un chargé d’affaires peut installer les jeux initiaux (CVC, GTB, électricité) ou créer les ouvrages de l’entreprise.'} />
        )}
        {r.data && !!r.data.ouvrages.length && !groupes.length && <EtatVide icone={<Search />} titre="Aucun ouvrage ne correspond" />}
        {groupes.map((g) => (
          <section key={g.cat} className="ouvrages-groupe" aria-label={g.libelle}>
            <h3 className="ouvrages-groupe-titre">{g.libelle}</h3>
            <ul className="ouvrages-liste">
              {g.ouvrages.map((o) => (
                <li key={o.id} className="ouvrages-ligne">
                  <div className="pile" style={{ gap: 2, minWidth: 0 }}>
                    <span className="ouvrages-libelle">{o.label}</span>
                    <span className="tres-petit discret"><span className="mono">{o.code}</span> — {pluriel(o.composants.length, 'composant')} — temps du {date(o.dated)}</span>
                    <span className="rangee" style={{ gap: 6 }}><BadgeMetier m={o.metier} /><BadgesTemps o={o} /></span>
                  </div>
                  <div className="ouvrages-ligne-droite">
                    <span className="chiffre petit">{heuresOuvrage(o)}</span>
                    <Bouton compact icone={<Pencil aria-hidden />} onClick={() => setEdite(o)}>{ecrire ? 'Régler' : 'Détail'}</Bouton>
                  </div>
                </li>
              ))}
            </ul>
          </section>
        ))}
      </Panneau>
      <Alerte titre="Ouvrages types : quantités et temps, jamais de prix">
        Un ouvrage regroupe des composants (quantité par unité d’ouvrage, heures de pose) et une liste « penser à tout » proposée en lignes
        « À renseigner ». Temps des splits : FactureCo (codes T01 à T21). Tout ce qui est marqué « Estimation — à valider » (DRV, aéraulique,
        hydraulique, GTB et domotique, électricité) est une estimation raisonnée, expliquée composant par composant : votre référent la confirme
        ou la règle. Installer un jeu ne crée jamais de doublon et ne modifie jamais un temps que vous avez réglé. Chaque entreprise règle ses
        temps ici, sans toucher aux autres.
      </Alerte>
      {r.data && <DialogueEditionOuvrage ouvrage={edite} fermer={() => setEdite(null)} categories={r.data.categories} ecrire={ecrire} />}
    </div>
  );
}

// ─── Création / réglage d'un ouvrage ───
interface FormComposant {
  cle: string; role: 'base' | 'penser_a_tout'; section: Section; label: string; reference: string; unit: string; qty: string; hours: string;
  supply: boolean; perUnit: boolean; timeCode: string | null; note: string | null; photoRequired: boolean;
}
let compteur = 0;
const nouvelleCle = () => `c${++compteur}`;
const versForm = (c: Composant): FormComposant => ({ cle: nouvelleCle(), role: c.role, section: c.section, label: c.label, reference: c.reference ?? '', unit: c.unit,
  qty: versSaisie(c.qty_per_unit), hours: versSaisie(c.hours_per_unit), supply: c.supply, perUnit: c.per_unit, timeCode: c.time_code, note: c.note, photoRequired: !!c.photo_required });
const composantVide = (): FormComposant => ({ cle: nouvelleCle(), role: 'base', section: 'materiel', label: '', reference: '', unit: 'u', qty: '1', hours: '',
  supply: true, perUnit: true, timeCode: null, note: null, photoRequired: false });

function DialogueEditionOuvrage({ ouvrage, fermer, categories, ecrire }: { ouvrage: Ouvrage | null | 'nouveau'; fermer: () => void;
  categories: Record<string, string>; ecrire: boolean }) {
  const qc = useQueryClient();
  const notifier = useNotifier();
  const ouvert = ouvrage !== null;
  const ex = ouvrage && ouvrage !== 'nouveau' ? ouvrage : null;
  const [f, setF] = useState({ code: '', label: '', category: 'autre', unit: 'u', description: '', reminders: '', estimate: false, active: true, crew: '' });
  const [comps, setComps] = useState<FormComposant[]>([]);
  const [erreur, setErreur] = useState<string | null>(null);
  useEffect(() => {
    if (!ouvert) return;
    setErreur(null);
    setF(ex ? { code: ex.code, label: ex.label, category: ex.category, unit: ex.unit, description: ex.description ?? '', reminders: ex.reminders ?? '',
      estimate: ex.estimate, active: ex.active, crew: ex.crew_min ? String(ex.crew_min) : '' }
      : { code: '', label: '', category: 'autre', unit: 'u', description: '', reminders: '', estimate: false, active: true, crew: '' });
    setComps(ex ? ex.composants.map(versForm) : [composantVide()]);
  }, [ouvert, ex]);
  const m = useMutation({
    mutationFn: () => {
      const corps = { code: f.code, label: f.label, category: f.category, unit: f.unit || 'u', description: f.description || null, reminders: f.reminders || null,
        estimate: f.estimate, active: f.active, crewMin: f.crew ? Number(f.crew) : null,
        composants: comps.map((c) => ({ role: c.role, section: c.section, label: c.label, reference: c.reference || null, unit: c.unit || 'u', qtyPerUnit: c.qty,
          hoursPerUnit: c.hours.trim() === '' ? null : c.hours, supply: c.supply, perUnit: c.perUnit, timeCode: c.timeCode, note: c.note, photoRequired: c.photoRequired })) };
      return ex ? api.patch(`/api/ouvrages/${ex.id}`, corps) : api.post('/api/ouvrages', corps);
    },
    onSuccess: () => { notifier(ex ? 'Ouvrage réglé. Les temps sont datés d’aujourd’hui.' : 'Ouvrage créé.'); void qc.invalidateQueries({ queryKey: cleListe }); fermer(); },
    onError: (e) => setErreur(messageErreur(e)),
  });
  const maj = (cle: string, x: Partial<FormComposant>) => setComps(comps.map((c) => (c.cle === cle ? { ...c, ...x } : c)));
  const invalide = comps.some((c) => !c.label.trim() || !c.qty.trim() || !saisieValide(c.qty) || !saisieValide(c.hours) || Number(c.qty.replace(',', '.')) === 0);
  const soumettre = (e: FormEvent) => {
    e.preventDefault();
    if (invalide) { setErreur('Chaque composant a une désignation, une quantité non nulle et des heures lisibles (vide = à renseigner).'); return; }
    m.mutate();
  };
  const optsCat = Object.entries(categories).map(([valeur, libelle]) => ({ valeur, libelle }));
  if (!ecrire && ex) {
    return (
      <Dialogue titre={ex.label} ouvert={ouvert} fermer={fermer} large pied={<Bouton onClick={fermer}>Fermer</Bouton>}>
        <ResumeOuvrage o={ex} />
      </Dialogue>
    );
  }
  return (
    <Dialogue titre={ex ? `Régler l’ouvrage ${ex.code}` : 'Nouvel ouvrage type'} ouvert={ouvert} fermer={fermer} large
      pied={<><Bouton onClick={fermer}>Annuler</Bouton><Bouton variante="principal" type="submit" form="form-ouvrage" chargement={m.isPending}>Enregistrer</Bouton></>}>
      <form id="form-ouvrage" className="pile" onSubmit={soumettre}>
        {erreur && <Alerte ton="danger">{erreur}</Alerte>}
        {ex && <p className="petit secondaire">Origine des temps : {ex.time_source} — {date(ex.dated)}.</p>}
        <div className="grille-2">
          <Champ libelle="Code" mono required maxLength={40} value={f.code} onChange={(e) => setF({ ...f, code: e.target.value })} />
          <Champ libelle="Libellé" required maxLength={300} value={f.label} onChange={(e) => setF({ ...f, label: e.target.value })} />
          <ChampChoix libelle="Catégorie" options={optsCat} value={f.category} onChange={(e) => setF({ ...f, category: e.target.value })} />
          <Champ libelle="Unité de l’ouvrage" maxLength={20} value={f.unit} onChange={(e) => setF({ ...f, unit: e.target.value })} aide="u, ml, kg, m², circuit…" />
          <ChampChoix libelle="Personnes minimum" value={f.crew} onChange={(e) => setF({ ...f, crew: e.target.value })} vide="Non précisé"
            options={Array.from({ length: 10 }, (_, k) => ({ valeur: String(k + 1), libelle: `${k + 1} personne${k ? 's' : ''}` }))}
            aide="Ouvrage qui se fait obligatoirement à plusieurs (levage, manutention lourde). Sert au chantier." />
        </div>
        <ChampTexte libelle="Description" rows={2} maxLength={4000} value={f.description} onChange={(e) => setF({ ...f, description: e.target.value })} />
        <ChampTexte libelle="À prévoir aussi (rappels affichés à l’insertion)" rows={2} maxLength={4000} value={f.reminders} onChange={(e) => setF({ ...f, reminders: e.target.value })} />
        <div className="rangee" style={{ gap: 16 }}>
          <label className="case"><input type="checkbox" checked={f.estimate} onChange={(e) => setF({ ...f, estimate: e.target.checked })} /> Temps estimés, à valider</label>
          <label className="case"><input type="checkbox" checked={f.active} onChange={(e) => setF({ ...f, active: e.target.checked })} /> Proposé dans les devis</label>
        </div>
        <h3 className="ouvrages-sous-titre">Composants</h3>
        <ol className="ouvrages-composants">
          {comps.map((c, i) => (
            <li key={c.cle} className="ouvrages-composant">
              <div className="grille-3">
                <Champ libelle={`Désignation ${i + 1}`} required maxLength={500} value={c.label} onChange={(e) => maj(c.cle, { label: e.target.value })} />
                <ChampChoix libelle="Rôle" value={c.role} onChange={(e) => maj(c.cle, { role: e.target.value as FormComposant['role'] })}
                  options={[{ valeur: 'base', libelle: 'Base de l’ouvrage' }, { valeur: 'penser_a_tout', libelle: 'Penser à tout (proposé)' }]} />
                <ChampChoix libelle="Section du devis" options={OPTIONS_SECTIONS} value={c.section} onChange={(e) => maj(c.cle, { section: e.target.value as Section })} />
                <Champ libelle="Quantité par unité d’ouvrage" inputMode="decimal" required value={c.qty} onChange={(e) => maj(c.cle, { qty: e.target.value })}
                  aria-invalid={!saisieValide(c.qty) || !c.qty.trim() || undefined} />
                <Champ libelle="Unité" maxLength={20} value={c.unit} onChange={(e) => maj(c.cle, { unit: e.target.value })} />
                <Champ libelle="Heures par unité" inputMode="decimal" value={c.hours} placeholder="À renseigner" onChange={(e) => maj(c.cle, { hours: e.target.value })}
                  aria-invalid={!saisieValide(c.hours) || undefined} aide={c.timeCode ? `Code ${c.timeCode}` : 'Vide = temps à renseigner'} />
                <Champ libelle="Référence catalogue" mono maxLength={120} value={c.reference} onChange={(e) => maj(c.cle, { reference: e.target.value })}
                  aide="Référence exacte : le prix daté du catalogue est repris" />
              </div>
              <div className="rangee" style={{ gap: 16 }}>
                <label className="case"><input type="checkbox" checked={c.supply} onChange={(e) => maj(c.cle, { supply: e.target.checked })} /> Fourniture à chiffrer</label>
                <label className="case"><input type="checkbox" checked={!c.perUnit} onChange={(e) => maj(c.cle, { perUnit: !e.target.checked })} /> Quantité fixe</label>
                <label className="case"><input type="checkbox" checked={c.photoRequired} onChange={(e) => maj(c.cle, { photoRequired: e.target.checked })} /> Photo demandée sur chantier</label>
                <Bouton compact variante="fantome" icone={<Trash2 aria-hidden />} disabled={comps.length === 1} onClick={() => setComps(comps.filter((x) => x.cle !== c.cle))}>
                  Retirer
                </Bouton>
              </div>
              {c.note && <p className="tres-petit discret">{c.note}</p>}
            </li>
          ))}
        </ol>
        <Bouton compact icone={<Plus aria-hidden />} onClick={() => setComps([...comps, composantVide()])} disabled={comps.length >= 60}>Ajouter un composant</Bouton>
      </form>
    </Dialogue>
  );
}

/** Lecture seule : composants, heures, rappels, origine. */
function ResumeOuvrage({ o, quantiteOuvrage }: { o: Ouvrage; quantiteOuvrage?: number }) {
  const base = o.composants.filter((c) => c.role === 'base');
  return (
    <div className="pile">
      <p className="petit secondaire">{o.description}</p>
      <p className="tres-petit discret">Origine des temps : {o.time_source} — {date(o.dated)}.</p>
      <span className="rangee" style={{ gap: 6 }}><BadgeMetier m={o.metier} /><BadgesTemps o={o} /></span>
      <p className="tres-petit discret">Personnes minimum : {o.crew_min ? o.crew_min : 'non précisé'}.</p>
      <ul className="ouvrages-resume">
        {base.map((c) => (
          <li key={c.id}>
            <span className="ouvrages-resume-libelle">{c.label}</span>
            <span className="tres-petit secondaire chiffre">
              {quantiteOuvrage !== undefined
                ? `${quantite(String(c.per_unit ? Number(c.qty_per_unit) * quantiteOuvrage : Number(c.qty_per_unit)))} ${c.unit}`
                : `${quantite(c.qty_per_unit)} ${c.unit}${c.per_unit ? ` / ${o.unit}` : ' (fixe)'}`}
              {' — '}{c.hours_per_unit === null ? 'temps à renseigner' : `${heures(c.hours_per_unit)} / ${c.unit}`}
              {c.time_code ? ` — ${c.time_code}` : ''}{!c.supply ? ' — main-d’œuvre seule' : ''}{c.photo_required ? ' — photo demandée' : ''}
            </span>
          </li>
        ))}
      </ul>
      {o.reminders && <Alerte ton="info" titre="À prévoir aussi">{o.reminders}</Alerte>}
    </div>
  );
}

// ═══ Insertion dans un devis ═══════════════════════════════════════════════════════════════════
export function DialogueOuvrage({ ouvert, fermer, vue, inserer }: { ouvert: boolean; fermer: () => void; vue: VueRevision;
  inserer: (corps: { workItemId: string; quantity: string; laborRateCode: string | null; penserATout: string[] }) => Promise<unknown> }) {
  const r = useQuery({ queryKey: cleListe, queryFn: chargerListe, enabled: ouvert });
  const [filtre, setFiltre] = useState('');
  const [choisi, setChoisi] = useState<string | null>(null);
  const [q, setQ] = useState('1');
  const [taux, setTaux] = useState('');
  const [penser, setPenser] = useState<string[]>([]);
  const [erreur, setErreur] = useState<string | null>(null);
  const [envoi, setEnvoi] = useState(false);
  const actifs = vue.taux_horaires.filter((t) => t.active);
  const o = r.data?.ouvrages.find((x) => x.id === choisi) ?? null;
  useEffect(() => { if (ouvert) { setChoisi(null); setFiltre(''); setErreur(null); } }, [ouvert]);
  useEffect(() => {
    if (!o) return;
    setQ('1'); setPenser(o.composants.filter((c) => c.role === 'penser_a_tout').map((c) => c.id));
    setTaux(o.labor_rate_code && actifs.some((t) => t.code === o.labor_rate_code) ? o.labor_rate_code : actifs.length === 1 ? actifs[0].code : '');
  }, [choisi]); // eslint-disable-line react-hooks/exhaustive-deps
  const liste = (r.data?.ouvrages ?? []).filter((x) => x.active && (!filtre.trim() || `${x.code} ${x.label}`.toLowerCase().includes(filtre.trim().toLowerCase())));
  const qOk = saisieValide(q) && q.trim() !== '' && Number(q.replace(',', '.').replace(/\s/g, '')) > 0;
  const nbLignes = o ? o.composants.filter((c) => c.role === 'base').length + penser.length : 0;
  const valider = async () => {
    if (!o) return;
    setEnvoi(true); setErreur(null);
    try { await inserer({ workItemId: o.id, quantity: q, laborRateCode: taux || null, penserATout: penser }); fermer(); }
    catch (x) { setErreur(messageErreur(x)); } finally { setEnvoi(false); }
  };
  return (
    <Dialogue titre={o ? o.label : 'Insérer un ouvrage type'} ouvert={ouvert} fermer={fermer} large
      pied={o ? <>
        <Bouton onClick={() => setChoisi(null)}>Autre ouvrage</Bouton>
        <Bouton variante="principal" disabled={!qOk} chargement={envoi} onClick={valider}>{`Insérer ${pluriel(nbLignes, 'ligne')}`}</Bouton>
      </> : <Bouton onClick={fermer}>Fermer</Bouton>}>
      {!o && (
        <div className="pile">
          <Champ libelle="Rechercher un ouvrage" value={filtre} onChange={(e) => setFiltre(e.target.value)} placeholder="Ex. cassette, point GTB, tableau divisionnaire" />
          {r.isLoading && <Chargement lignes={3} />}
          {r.isError && <EtatErreur erreur={r.error} reessayer={() => r.refetch()} />}
          {r.data && !r.data.ouvrages.length && (
            <EtatVide icone={<Boxes />} titre="Aucun ouvrage type" texte="Installez les jeux initiaux (CVC, GTB, électricité) dans Études › Catalogue de prix › Ouvrages types (chargés d’affaires et administrateurs)." />
          )}
          {r.data && !!r.data.ouvrages.length && !liste.length && <EtatVide icone={<Search />} titre="Aucun ouvrage ne correspond" />}
          {!!liste.length && (
            <ul className="devis-resultats">
              {liste.map((x) => (
                <li key={x.id} className="devis-resultat">
                  <div className="pile" style={{ gap: 2, minWidth: 0, flex: 1 }}>
                    <span>{x.label}</span>
                    <span className="tres-petit discret"><span className="mono">{x.code}</span> — {heuresOuvrage(x)}</span>
                    <span className="rangee" style={{ gap: 6 }}><BadgeMetier m={x.metier} /><BadgesTemps o={x} /></span>
                  </div>
                  <Bouton compact variante="principal" onClick={() => setChoisi(x.id)}>Choisir</Bouton>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
      {o && (
        <div className="pile">
          {erreur && <Alerte ton="danger">{erreur}</Alerte>}
          <div className="grille-2">
            <Champ libelle={`Quantité (${o.unit})`} inputMode="decimal" value={q} onChange={(e) => setQ(e.target.value)} aria-invalid={!qOk || undefined} required />
            <ChampChoix libelle="Taux horaire du catalogue" vide="Aucun (heures à valoriser plus tard)" value={taux} onChange={(e) => setTaux(e.target.value)}
              options={actifs.map((t) => ({ valeur: t.code, libelle: `${t.code} — ${t.label}` }))}
              aide={actifs.length ? undefined : 'Aucun taux horaire actif : les heures resteront « À renseigner ».'} />
          </div>
          <ResumeOuvrage o={o} quantiteOuvrage={qOk ? Number(q.replace(',', '.').replace(/\s/g, '')) : undefined} />
          {o.composants.some((c) => c.role === 'penser_a_tout') && (
            <fieldset className="ouvrages-penser">
              <legend className="champ-libelle">Penser à tout — lignes proposées « À renseigner »</legend>
              {o.composants.filter((c) => c.role === 'penser_a_tout').map((c) => (
                <label key={c.id} className="case">
                  <input type="checkbox" checked={penser.includes(c.id)} onChange={(e) => setPenser(e.target.checked ? [...penser, c.id] : penser.filter((x) => x !== c.id))} />
                  <span>{c.label} <span className="tres-petit discret">— {quantite(c.qty_per_unit)} {c.unit}{c.per_unit ? ` / ${o.unit}` : ''}{c.hours_per_unit === null ? ', temps à renseigner' : Number(c.hours_per_unit) > 0 ? `, ${heures(c.hours_per_unit)} / ${c.unit}` : ''}</span></span>
                </label>
              ))}
            </fieldset>
          )}
          <p className="tres-petit discret">Fournitures : prix du catalogue par référence exacte, sinon « À renseigner ». Main-d’œuvre seule : heures × taux horaire choisi.
            Sections du devis : {[...new Set(o.composants.map((c) => LIBELLES_SECTIONS[c.section]))].join(', ')}.</p>
        </div>
      )}
    </Dialogue>
  );
}
